import { createRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'

import { Button, Checkbox, FormRow, Label, QueryDropdown, TextField } from 'basic-components'
import { Add } from 'basic-components/icons'
import Query from 'dashboard-services/api/Query'
import { globalActions } from 'primary-components'

import getApiConfig from 'api/ApiConfig'
import { groupsDatalakeQueries } from 'queries/modules/datalake'

import MetadataSet from './MetadataSet'

import './Details.scss'

const EMPTY_ARRAY = []

const toTagOptions = groupTags => (groupTags || EMPTY_ARRAY).map(({ tagId, tagName }) => tagId === undefined
  ? { value: tagName, label: tagName, __isNew__: true }
  : { value: tagId, label: tagName })

const getErrorValidator = ({ errors, field }) => [{
  message: errors.find(error => error.field === field)?.message || "",
  validator: () => !errors.some(error => error.field === field)
}]

const useGroupTagsOptions = () => {
  const dispatch = useDispatch(),
        [groupTagsInput, setGroupTagsInput] = useState(""),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
        , [dispatch]),
        { data, isPending, fetchNextPage, isFetchingNextPage, hasNextPage } = groupsDatalakeQueries.useSearchGroupsTags({ getQueryBuilder, input: groupTagsInput, shouldBeLimited }),
        options = useMemo(() => (data?.pages || []).flatMap(page => page?.items || []).map(item => ({ value: item?.tagId, label: item?.tagName })), [data]),
        toReturn = useMemo(() => ({ options, isFetching: isPending || isFetchingNextPage, fetchNextPage, hasNextPage, setGroupTagsInput }), [options, isPending, isFetchingNextPage, fetchNextPage, hasNextPage])

  return toReturn
}

const useScrollToNewSet = ({ count }) => {
  const [refs, setRefs] = useState(EMPTY_ARRAY),
        previousCount = useRef(count)

  useEffect(() => {
    setRefs(refs => Array(count).fill().map((_, index) => refs[index] || createRef()))
  }, [count])

  useEffect(() => {
    if(count > previousCount.current) {
      refs[count - 1]?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    }
    previousCount.current = count
  }, [count, refs])

  return refs
}

const Details = ({ description, errors, fieldsToDisplay, groupName, groupTags, isDataTimeFrameEnabled, isLoading = false, isNameDisabled, menuPortalTarget = undefined, onAddField, onChangeDataTimeFrame, onChangeDescription, onChangeField, onChangeForwardCurves, onChangeGroupName, onChangeGroupTags, onRemoveField, shouldBeForwardCurvesGroup }) => {
  const { options: groupTagsOptions, isFetching, fetchNextPage, hasNextPage, setGroupTagsInput } = useGroupTagsOptions(),
        defaultGroupTags = useMemo(() => toTagOptions(groupTags), [groupTags]),
        nameValidator = useMemo(() => getErrorValidator({ errors, field: "name" }), [errors]),
        descriptionValidator = useMemo(() => getErrorValidator({ errors, field: "description" }), [errors]),
        nameRef = useRef(),
        descriptionRef = useRef(),
        metadataRefs = useScrollToNewSet({ count: fieldsToDisplay.length })

  useEffect(() => {
    errors.some(error => error.field === "name") && nameRef.current?.validateForForm()
    errors.some(error => error.field === "description") && descriptionRef.current?.validateForForm()
  }, [errors])

  return (
    <div className="data-lake__group-dialog__details">
      <FormRow isVertical>
        <Label required>Name</Label>
        <TextField
            defaultValue={groupName}
            disabled={isNameDisabled || isLoading}
            name="groupName"
            onChange={onChangeGroupName}
            ref={nameRef}
            validators={nameValidator}
            variant="border"
        />
      </FormRow>
      <FormRow isVertical>
        <Label>Description</Label>
        <TextField
            defaultValue={description}
            disabled={isLoading}
            name="description"
            onChange={onChangeDescription}
            ref={descriptionRef}
            validators={descriptionValidator}
            variant="border"
        />
      </FormRow>
      <FormRow isVertical>
        <Label>Tags</Label>
        <QueryDropdown
            className="data-lake__group-dialog__details__tags"
            creatable
            defaultValue={defaultGroupTags}
            disabled={isLoading}
            fetchNextPage={fetchNextPage}
            fixed
            hasMore={hasNextPage}
            isFetching={isFetching}
            menuPortalTarget={menuPortalTarget}
            multi
            name="tags"
            onChange={onChangeGroupTags}
            onInputChange={setGroupTagsInput}
            options={groupTagsOptions}
            variant="border"
        />
      </FormRow>
      <FormRow className="data-lake__group-dialog__details__checkbox-row">
        <Checkbox
            checked={isDataTimeFrameEnabled}
            disabled={isLoading}
            name="isDataTimeFrameEnabled"
            onChange={onChangeDataTimeFrame}
        >
          Require Timeframe for files uploaded to the group
        </Checkbox>
      </FormRow>
      <FormRow className="data-lake__group-dialog__details__checkbox-row">
        <Checkbox
            checked={shouldBeForwardCurvesGroup}
            disabled={isLoading}
            name="shouldBeForwardCurvesGroup"
            onChange={onChangeForwardCurves}
        >
          Mark as group for Forward Curves
        </Checkbox>
      </FormRow>
      <div className="data-lake__group-dialog__details__metadata-title">Metadata</div>
      <div
          className="data-lake__group-dialog__details__metadata-adder"
          data-testid="group-dialog-add-metadata"
          onClick={isLoading ? undefined : onAddField}
      >
        <Button
            disabled={isLoading}
            size="small"
            type="button"
            variant="text"
        >
          <Add
              color="blue-bright"
              height={16}
              width={16}
          />
          Add metadata
        </Button>
      </div>
      {fieldsToDisplay.map((field, index) => (
        <MetadataSet
            {...field}
            disabled={isLoading}
            index={index}
            key={index}
            menuPortalTarget={menuPortalTarget}
            onChange={onChangeField}
            onRemove={onRemoveField}
            ref={metadataRefs[index]}
        />
      ))}
    </div>
  )
}

Details.propTypes = {
  description: PropTypes.string,
  errors: PropTypes.array.isRequired,
  fieldsToDisplay: PropTypes.array.isRequired,
  groupName: PropTypes.string,
  groupTags: PropTypes.array.isRequired,
  isDataTimeFrameEnabled: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  isNameDisabled: PropTypes.bool.isRequired,
  menuPortalTarget: PropTypes.object,
  onAddField: PropTypes.func.isRequired,
  onChangeDataTimeFrame: PropTypes.func.isRequired,
  onChangeDescription: PropTypes.func.isRequired,
  onChangeField: PropTypes.func.isRequired,
  onChangeForwardCurves: PropTypes.func.isRequired,
  onChangeGroupName: PropTypes.func.isRequired,
  onChangeGroupTags: PropTypes.func.isRequired,
  onRemoveField: PropTypes.func.isRequired,
  shouldBeForwardCurvesGroup: PropTypes.bool.isRequired
}

export default memo(Details)
